import { isAxiosError } from 'axios';
import { apiClient, ApiResponse } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';

/** 사업자 유형. V134 이전 사용자는 null(미등록) */
export const BUSINESS_OWNER_TYPE = {
  INDIVIDUAL: 'INDIVIDUAL',
  CORPORATE: 'CORPORATE',
} as const;

export type BusinessOwnerType =
  (typeof BUSINESS_OWNER_TYPE)[keyof typeof BUSINESS_OWNER_TYPE];

// 플랫폼 사용자 관련 타입 정의
export interface PlatformUserSearchRequest {
  email?: string;
  phoneNumber?: string;
  ceoName?: string;
  companyName?: string;
  isBusinessVerified?: boolean;
  isFinancialVerified?: boolean;
  isActive?: boolean;
  promoLicenseStatus?: string;
  /** @deprecated 레거시는 salesApprovalStatus 문자열을 사용 */
  isSalesApproved?: boolean;
  /** 영업 승인 상태 (TRANSACTION_POSSIBLE | SETTLEMENT_POSSIBLE) */
  salesApprovalStatus?: string;
  trainingStatus?: string;
  /**
   * 사업자 유형 필터. 생략하면 전체.
   * 미지정(null)만 거르는 값은 없다.
   */
  businessOwnerType?: BusinessOwnerType;
}

export interface Pageable {
  page: number;
  size: number;
  sort?: string[];
}

export interface PlatformUserResponse {
  id?: number;
  userId?: number;
  email: string;
  registeredAt: string;
  nickname: string;
  phoneNumber: string;
  businessLicenseFile: string;
  businessLicenseNumber: string;
  bankName: string;
  accountHolder: string;
  bankAccountNumber: string;
  promoLicenseFile: string;
  promoLicenseNumber: string;
  promoLicenseValidTo: string;
  promoLicenseVerified: boolean;
  trainingCertificateFile: string;
  trainingValidFrom: string;
  trainingValidTo: string;
  trainingVerified: boolean;
  termsAgreements: string;
  withdrawnAt?: string;
  remarks: string;
  taxInvoiceEmail: string;
  adjustFeeRate: number;
  ceoName: string;
  companyName: string;
  /**
   * 사업자 유형. 상세와 같은 user_profile_detail 값.
   * 미지정이면 null.
   */
  businessOwnerType?: BusinessOwnerType | null;
  address: string;
  bankbookCopyFile?: string;
  signatureImage?: string;
  promoLicenseStatus:
    | 'NOT_REGISTERED'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'EXPIRED';
  trainingStatus:
    | 'NOT_REGISTERED'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'EXPIRED';
  status: string;
  financialVerificationStatus: string;
  salesApprovalStatus: string;
  businessVerificationStatus: string;
  active: boolean;
  businessVerified: boolean;
  financialVerified: boolean;
  salesApproved: boolean;
  /** 목록 위탁계약 컬럼용 — 레거시 usersInfo와 동일 */
  contractInfo?:
    | import('@/entities/contract/api/consignment-contract.api').UserContractInfo
    | null;
}

export interface PageResponse<T> {
  content: T[];
  pageNo: number;
  pageSize: number;
  isLast: boolean;
  totalCount: number;
}

export interface PlatformUserDetailResponse {
  userId: number;
  email: string;
  isActive: boolean;
  registeredAt: string;
  nickname: string;
  phoneNumber: string;
  businessLicenseFile: string;
  businessLicenseNumber: string;
  /**
   * 사업자 유형. 미등록(기존 사용자·OCR 실패)이면 null.
   * 개인사업자일 때만 birthDate 값이 있다.
   */
  businessOwnerType?: BusinessOwnerType | null;
  /** 대표자 생년월일(yyyy-MM-dd). 법인·미등록이면 null */
  birthDate?: string | null;
  companyName: string;
  ceoName: string;
  businessType?: string;
  businessClass?: string;
  address: string;
  isBusinessVerified: boolean;
  isFinancialVerified: boolean;
  bankName: string;
  accountHolder: string;
  bankAccountNumber: string;
  taxInvoiceEmail: string;
  adjustFeeRate: number;
  promoLicenseFile: string;
  promoLicenseNumber: string;
  promoLicenseValidFrom: string;
  promoLicenseValidTo: string;
  promoLicenseVerified: boolean;
  trainingCertificateFile: string;
  trainingVerified: boolean;
  trainingValidFrom: string;
  trainingValidTo: string;
  isSalesApproved: boolean;
  bankbookCopyFile?: string;
  withdrawnAt?: string;
  remarks: string;
  /** @deprecated 레거시 JSON 묶음. 종류별 일시가 없어 termsAgreementItems 를 쓴다 */
  termsAgreements: any;
  /** 약관 종류별 동의 정보. 구버전 서버 응답에는 없다 */
  termsAgreementItems?: PlatformUserTermsAgreementItem[];
  clinicNames: string[];
  promoLicenseStatus:
    | 'NOT_REGISTERED'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'EXPIRED';
  trainingStatus:
    | 'NOT_REGISTERED'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'EXPIRED';
  status: string;
  financialVerificationStatus: string;
  salesApprovalStatus: string;
  businessVerificationStatus: string;
  contractInfo?:
    | import('@/entities/contract/api/consignment-contract.api').UserContractInfo
    | null;
  contractStatus?: string;
  contractCompletedAt?: string;
  contractDocumentNumber?: string;
  consignmentContractFile?: string;
}

export interface TermsAgreementItem {
  termId: string;
  agreed: boolean;
}

export interface TermsAgreementsResponse {
  agreements?: TermsAgreementItem[];
}

/**
 * 약관 종류별 동의 정보. 서버가 terms_type 기준으로 항상 전 종류를 내려준다.
 * 버전 단위 기록이 없는 기존 가입자는 agreedAt 이 가입 시 공통 동의 일시이고
 * termsVersion·currentVersion 이 null 이다.
 */
export interface PlatformUserTermsAgreementItem {
  /** 약관 종류 코드 (예: TERMS_OF_SERVICE) */
  type: string;
  /** 약관 종류 이름 (예: 루다링크 이용약관) */
  name: string;
  required: boolean;
  /** 동의 기록이 없으면 null */
  agreed: boolean | null;
  /** `yyyy-MM-dd HH:mm:ss`. 기록이 없으면 null */
  agreedAt: string | null;
  termsVersion: number | null;
  /** false 면 재동의 대상. 기록이 없으면 null */
  currentVersion: boolean | null;
}

/** 로그인 수단 표의 행 종류. 서버가 이 순서로 5행 고정 반환 */
export const LOGIN_METHOD_TYPE = {
  KAKAO: 'KAKAO',
  NAVER: 'NAVER',
  GOOGLE: 'GOOGLE',
  APPLE: 'APPLE',
  EMAIL: 'EMAIL',
} as const;

export type LoginMethodType =
  (typeof LOGIN_METHOD_TYPE)[keyof typeof LOGIN_METHOD_TYPE];

/** 탈퇴 시 제공자 연결 해제(revoke) 결과. 탈퇴 계정의 연결됐던 SNS 행에만 있다 */
export interface PlatformUserWithdrawalUnlink {
  completed: boolean;
  completedAt: string | null;
}

export interface PlatformUserLoginMethod {
  method: LoginMethodType;
  /** SNS 는 연결 여부, EMAIL 은 비밀번호 설정 여부 */
  linked: boolean;
  /** SNS 는 제공자 이메일, EMAIL 은 계정 이메일. 미연결이면 null */
  accountEmail: string | null;
  /** 처음 가입한 수단. 지금 미연결이어도 true 일 수 있다 */
  signupMethod: boolean;
  /** Apple 비공개(릴레이) 이메일 여부 */
  privateEmail: boolean;
  /** SNS 연결 일시 / 비밀번호 설정 일시 */
  linkedAt: string | null;
  /** 그 수단으로 직접 로그인한 마지막 일시 */
  lastUsedAt: string | null;
  withdrawalUnlink: PlatformUserWithdrawalUnlink | null;
}

export interface PlatformUserLoginMethodsResponse {
  userId: number;
  /** true 면 전 값이 탈퇴 시점 스냅샷이다 */
  withdrawn: boolean;
  signup: {
    /** 서버가 모르는 가입 제공자 코드면 null */
    method: LoginMethodType | null;
    signedUpAt: string | null;
  };
  password: {
    set: boolean;
    setAt: string | null;
  };
  /** 접속 기록이 없으면 null */
  lastAccess: {
    accessedAt: string;
    /** 그 세션을 처음 만든 수단. 수단 기록(V149) 전 접속 로그면 null */
    method: LoginMethodType | null;
  } | null;
  methods: PlatformUserLoginMethod[];
}

export interface MaxMatchLiteUsageResponse {
  usageCount: number;
  maxLimit: number;
  remaining: number;
}

export interface PlatformUserDetailUpdateRequest {
  companyName?: string;
  ceoName?: string;
  isActive?: boolean;
  phoneNumber?: string;
  address?: string;
  taxInvoiceEmail?: string;
  businessType?: string;
  businessClass?: string;
  businessLicenseNumber?: string;
  /**
   * 사업자 유형. null·미입력이면 기존 값 유지.
   * 유형을 비우는 방법은 없다.
   */
  businessOwnerType?: BusinessOwnerType | null;
  /**
   * 대표자 생년월일(yyyy-MM-dd). 과거 날짜만.
   * null·미입력이면 기존 값 유지. CORPORATE 저장 시 서버가 null 로 비운다.
   * 요청·DB 모두 유형이 없는데 이 값만 보내면 400.
   */
  birthDate?: string | null;
  bankName?: string;
  bankAccountNumber?: string;
  promoLicenseFile?: string;
  promoLicenseStatus?:
    | 'NOT_REGISTERED'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'EXPIRED';
  promoLicenseValidFrom?: string;
  promoLicenseValidTo?: string;
  trainingCertificateFile?: string;
  trainingStatus?:
    | 'NOT_REGISTERED'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'EXPIRED';
  trainingValidFrom?: string;
  trainingValidTo?: string;
  adjustFeeRate?: number;
  accountHolder?: string;
  promoLicenseNumber?: string;
  salesApprovalStatus?:
    | 'TRANSACTION_IMPOSSIBLE'
    | 'TRANSACTION_POSSIBLE'
    | 'SETTLEMENT_POSSIBLE';
  businessVerificationStatus?:
    | 'NOT_REGISTERED'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'BUSINESS_TYPE_NOT_ALLOWED';
  financialVerificationStatus?:
    | 'NOT_REGISTERED'
    | 'NOT_VERIFIED'
    | 'VERIFICATION_FAILED'
    | 'VERIFIED';
  businessLicenseFile?: string;
  bankbookCopyFile?: string;
  remarks?: string;
}

export interface PresignedUrlRequest {
  filename: string;
}

export interface UploadResponse {
  presignedUrl: string;
  uploadKey: string;
  shortKey: string;
}

/** 사용자 저장 시 서류 파일 값이 업로드 URL 발급 응답의 uploadKey가 아니면 400 */
export const INVALID_UPLOAD_KEY_CODE = 'INVALID_UPLOAD_KEY';
export const REUPLOAD_FILE_MESSAGE = '파일을 다시 올려 주세요.';

/**
 * 사용자 서류(판촉 영업자 신고증·사업자등록증·통장 사본)로 올릴 수 있는 확장자.
 * 서버가 서명·미리보기·OCR 을 지원하는 형식만 받는다. HEIC·WebP 등은 업로드가 403 으로 실패하거나
 * 저장돼도 브라우저 미리보기·OCR 이 되지 않는다.
 */
export const USER_DOCUMENT_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf'] as const;
export const USER_DOCUMENT_ACCEPT = USER_DOCUMENT_EXTENSIONS.map(
  (extension) => `.${extension}`,
).join(',');
export const USER_DOCUMENT_TYPE_MESSAGE =
  'PNG·JPG·PDF 파일만 올릴 수 있습니다. 아이폰 사진(HEIC)은 JPG로 바꿔서 올려 주세요.';

/** 허용하지 않는 확장자면 서버에 업로드 URL 을 요청하기 전에 멈춘다 */
export const assertUserDocumentFile = (fileName: string): void => {
  const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
  if (!(USER_DOCUMENT_EXTENSIONS as readonly string[]).includes(extension)) {
    throw new Error(USER_DOCUMENT_TYPE_MESSAGE);
  }
};

/**
 * 서류 파일 값으로 저장할 S3 키(uploadKey)를 꺼낸다.
 *
 * 서버는 저장된 값을 그대로 S3 키로 서명해 다운로드 URL을 만든다.
 * shortKey·presignedUrl로 대체하면 열리지 않는 파일이 저장되므로, 없으면 업로드를 멈춘다.
 */
export const requireUploadKey = ({ uploadKey }: UploadResponse): string => {
  if (!uploadKey) {
    throw new Error(`업로드 정보를 받지 못했습니다. ${REUPLOAD_FILE_MESSAGE}`);
  }
  return uploadKey;
};

export const isInvalidUploadKeyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === INVALID_UPLOAD_KEY_CODE;
};

/** 판촉영업위탁계약 유형. 미입력 시 서버가 INTERNAL 로 저장한다. */
export type PromoContractType = 'INTERNAL' | 'EXTERNAL';

export interface PlatformUserBulkRegisterItem {
  email: string;
  phoneNumber: string;
  promoLicenseNumber: string;
  businessLicenseNumber: string;
  companyName: string;
  ceoName: string;
  address: string;
  taxInvoiceEmail: string;
  bankName: string;
  bankAccountNumber: string;
  promoContractType?: PromoContractType;
}

export interface PlatformUserBulkRegisterRequest {
  users: PlatformUserBulkRegisterItem[];
}

export interface PlatformUserBulkRegisterResultUser {
  userId: number;
  email: string;
  temporaryPassword: string;
}

export interface PlatformUserBulkRegisterResponse {
  users: PlatformUserBulkRegisterResultUser[];
  /** 구응답 호환. 없으면 users.length 를 사용한다. */
  createdCount?: number;
}

// 플랫폼 사용자 서비스
export const userService = {
  getUsers: async (
    searchRequest: PlatformUserSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<PlatformUserResponse>> => {
    const params: Record<string, string | number | boolean> = {
      page: pageable.page,
      size: pageable.size,
    };

    if (searchRequest.email) params.email = searchRequest.email;
    if (searchRequest.phoneNumber)
      params.phoneNumber = searchRequest.phoneNumber;
    if (searchRequest.ceoName) params.ceoName = searchRequest.ceoName;
    if (searchRequest.companyName)
      params.companyName = searchRequest.companyName;
    if (searchRequest.isBusinessVerified != null) {
      params.isBusinessVerified = searchRequest.isBusinessVerified;
    }
    if (searchRequest.isFinancialVerified != null) {
      params.isFinancialVerified = searchRequest.isFinancialVerified;
    }
    if (searchRequest.isActive != null)
      params.isActive = searchRequest.isActive;
    if (searchRequest.promoLicenseStatus) {
      params.promoLicenseStatus = searchRequest.promoLicenseStatus;
    }
    if (searchRequest.salesApprovalStatus) {
      params.salesApprovalStatus = searchRequest.salesApprovalStatus;
    } else if (searchRequest.isSalesApproved != null) {
      params.isSalesApproved = searchRequest.isSalesApproved;
    }
    if (searchRequest.trainingStatus) {
      params.trainingStatus = searchRequest.trainingStatus;
    }
    if (searchRequest.businessOwnerType) {
      params.businessOwnerType = searchRequest.businessOwnerType;
    }

    const response = await apiClient.get(createApiPath.users.list(), {
      params,
    });
    return response.data;
  },

  /**
   * 사용자 상세 — 사용자 ID 로 요청한 계정을 그대로 받는다.
   * 이메일 경로(`/platform-users/{email}`)는 같은 이메일 중 최신 계정만 돌려줘 서버에서 deprecated 됐다.
   */
  getUserDetail: async (
    userId: number,
  ): Promise<PlatformUserDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.users.detailById(userId),
    );
    return response.data;
  },

  /** 로그인 수단 카드 — 가입 수단·비밀번호 설정·최근 접속·연결된 수단 표 */
  getLoginMethods: async (
    userId: number,
  ): Promise<PlatformUserLoginMethodsResponse> => {
    const response = await apiClient.get(
      createApiPath.users.loginMethods(userId),
    );
    return response.data;
  },

  /**
   * 관리자 탈퇴 처리 — 성공 시 204(본문 없음), 대상이 없거나 이미 탈퇴면 404 `USER_NOT_FOUND`.
   * 서버가 앱 탈퇴와 같은 순서로 처리한다: 이메일 점유 해제·로그인 수단 스냅샷·이용정지·
   * SNS 연결 삭제 후 커밋 뒤 제공자 연결 해제(revoke). 그래서 같은 이메일·SNS 로 다시 가입할 수 있다.
   */
  withdrawUser: async (userId: number): Promise<void> => {
    await apiClient.post(createApiPath.users.withdraw(userId));
  },

  // 사용자 ID로 상세 정보 수정
  updateUserDetail: async (
    userId: number,
    updateData: PlatformUserDetailUpdateRequest,
  ): Promise<ApiResponse<void>> => {
    const response = await apiClient.put(
      createApiPath.users.detailById(userId),
      updateData,
    );
    return response.data;
  },

  // 교육 이수증 업로드용 Presigned URL 생성
  generateTrainingVerifiedPresignedUrl: async (
    request: PresignedUrlRequest,
  ): Promise<UploadResponse> => {
    const response = await apiClient.post(
      createApiPath.users.trainingPresignedUrl(),
      request,
    );
    return response.data;
  },

  // 판촉 영업자 신고증 업로드용 Presigned URL 생성
  generatePromoLicensePresignedUrl: async (
    request: PresignedUrlRequest,
  ): Promise<UploadResponse> => {
    const response = await apiClient.post(
      createApiPath.users.promoPresignedUrl(),
      request,
    );
    return response.data;
  },

  generateBusinessLicensePresignedUrl: async (
    request: PresignedUrlRequest,
  ): Promise<UploadResponse> => {
    const response = await apiClient.post(
      createApiPath.users.businessLicensePresignedUrl(),
      request,
    );
    return response.data;
  },

  generateBankbookPresignedUrl: async (
    request: PresignedUrlRequest,
  ): Promise<UploadResponse> => {
    const response = await apiClient.post(
      createApiPath.users.bankbookPresignedUrl(),
      request,
    );
    return response.data;
  },

  resetBusinessLicense: async (userId: number): Promise<void> => {
    await apiClient.delete(createApiPath.users.resetBusinessLicense(userId));
  },

  resetPromoLicense: async (userId: number): Promise<void> => {
    await apiClient.delete(createApiPath.users.resetPromoLicense(userId));
  },

  getMaxMatchLiteCurrentMonthUsage: async (
    userId: number,
  ): Promise<MaxMatchLiteUsageResponse> => {
    const response = await apiClient.get(
      createApiPath.users.maxMatchLiteCurrentMonth(userId),
    );
    return response.data;
  },

  resetMaxMatchLiteCurrentMonthUsage: async (
    userId: number,
    reason: string,
  ): Promise<void> => {
    await apiClient.post(createApiPath.users.maxMatchLiteUsageReset(userId), {
      reason,
    });
  },

  /** 플랫폼 사용자 엑셀 일괄 등록 — 최대 500건, 전체 성공/실패 */
  bulkRegisterUsers: async (
    request: PlatformUserBulkRegisterRequest,
  ): Promise<PlatformUserBulkRegisterResponse> => {
    const response = await apiClient.post(createApiPath.users.bulk(), request);
    return response.data;
  },
};
